import { Celsius } from "@/libs/units/temperature";
import { Inches, Meters } from "../../units/length";
import type { MotorCoefInner, MotorCoefRR } from "./types";

export function getMotorSkinTemperatureF(
  motorLengthInches: number, // Motor Length
  casingID: number, // Casing ID
  downholeMotorTemperatureF: number, // Datum Temp in F
  mixtureSuperficialVelocityFTSEC: number, // Mixture Superficial Velocity
  gasSuperficialVelocityFTSEC: number, // Gas Superficial Velocity
  liquidSuperficialVelocityFTSEC: number,
  waterCutRatio: number,
  gasDensityLBMFT3: number, // gasDensityLBMFT3
  liquidDensityLBMFT3: number, // pliquid
  motorWindingTemperatureF: number, // Winding Temp Calculation
  liquidSpecificHeatCapacity: number,
  fluidViscosityCP: number,
  liquidViscosityCP: number,
  motorCoefInner: MotorCoefInner,
  equipmentCrossSectionFt2: number
) {
  // outer Motor Radius imperial
  const outerMotorRadiusImperial = Inches.toStandardUnit(
    motorCoefInner.outerDiameterInches / 2
  );
  // outer Motor Radius metric
  const outerMotorRadiusMetric = Meters.toThisUnit(outerMotorRadiusImperial);
  // inner Motor Radius metric
  const innerMotorRadiusMetric = outerMotorRadiusMetric - 0.011318;
  // outer Stator Diameter
  const outerStatorDiameter = Inches.toStandardUnit(motorCoefInner.statorOD);
  // outer Stator Diameter metric
  const outerStatorDiameterMetric = Meters.toThisUnit(outerStatorDiameter);
  // inner Stator Diameter imperial
  // =G15/12
  // G15 = motorCoefInner.StatorID
  const innerStatorDiameterImperial = Inches.toStandardUnit(
    motorCoefInner.statorID
  );
  // inner Stator Diameter metric
  const innerStatorDiameterMetric = Meters.toThisUnit(
    innerStatorDiameterImperial
  );
  // motor length ft
  const motorLengthFt = Inches.toStandardUnit(motorLengthInches);
  // motor length metric
  const motorLengthMetric = Meters.toThisUnit(motorLengthFt);
  // Area from equipment cross section
  const motorCrossSectionFt2 = equipmentCrossSectionFt2 / 10.764;
  const motorSurfaceAreaM2 =
    2 * Math.PI * outerMotorRadiusMetric * motorLengthMetric;
  // Motor Winding Temp
  const motorWindingTemp = Celsius.toThisUnit(motorWindingTemperatureF);
  // Fluid Temp @ Motor Base
  const fluidTempAtMotorBase = Celsius.toThisUnit(downholeMotorTemperatureF);
  // k1 (housing) - steel
  const k1 = 44;
  // k2
  // noSlipLiquidLamda =M172/M173
  const noSlipLiquidLamda =
    liquidSuperficialVelocityFTSEC / mixtureSuperficialVelocityFTSEC;
  // no_slip_gas =M171/M173
  const noSlipGas =
    gasSuperficialVelocityFTSEC / mixtureSuperficialVelocityFTSEC;
  // convert percentage to decimal
  const k2 =
    (0.609 * waterCutRatio + 0.159 * (1 - waterCutRatio)) * noSlipLiquidLamda +
    0.015 * noSlipGas;
  // Fluid Density_p - kg/m^3
  const pNoSlip =
    liquidDensityLBMFT3 * noSlipLiquidLamda +
    gasDensityLBMFT3 * (1 - noSlipLiquidLamda);
  // pNoSlip = M259
  const fluidDensity_p = pNoSlip * 16.018463;
  // Fluid Velocity - v
  const fluidVelocity = Meters.toThisUnit(mixtureSuperficialVelocityFTSEC);
  // Prandtl Number
  const prandtlNumber = (fluidViscosityCP * liquidSpecificHeatCapacity) / k2;
  // Reynolds Number
  const reynoldsNumber =
    (1488 * liquidDensityLBMFT3 * casingID * mixtureSuperficialVelocityFTSEC) /
    liquidViscosityCP;
  // Nusselt Number
  const nusseltNumber =
    reynoldsNumber < 2000
      ? 0.29 * Math.pow(reynoldsNumber, 0.8) * Math.pow(prandtlNumber, 0.5)
      : 0.8 * Math.pow(reynoldsNumber, 0.7) * Math.pow(prandtlNumber, 0.33);
  // fluid heat transfer coefficient - h
  const fluidHeatTransferCoefficient = (nusseltNumber * k2) / motorLengthMetric;
  // mass flow rate _m* - kg/s
  const massFlowRate_m = fluidDensity_p * fluidVelocity * motorCrossSectionFt2;
  // conduction resistance Rcyl_f
  const conductionResistanceRcylF =
    1 / (fluidHeatTransferCoefficient * massFlowRate_m);
  // conduction resistance Rcyl_s
  const conductionResistanceRcylS =
    Math.log(outerStatorDiameterMetric / innerStatorDiameterMetric) /
    (2 * Math.PI * motorLengthMetric * motorCoefInner.k3);
  // conduction resistance Rcyl_h
  const conductionResistanceRcylH =
    Math.log(outerMotorRadiusMetric / innerMotorRadiusMetric) /
    (2 * Math.PI * motorLengthMetric * k1);
  // Heat Transfer Q*
  const heatTransferQ =
    (motorWindingTemp - fluidTempAtMotorBase) /
    (conductionResistanceRcylF +
      conductionResistanceRcylS +
      conductionResistanceRcylH);
  // delta T_s
  const deltatS = conductionResistanceRcylS * heatTransferQ;
  // delta T_h
  const deltatH = conductionResistanceRcylH * heatTransferQ;
  // delta T
  const deltaT = motorWindingTemp - deltatS - deltatH;
  // Q'
  const convectiveHeatTransfer =
    conductionResistanceRcylF *
    (motorWindingTemp - deltaT) *
    motorSurfaceAreaM2;
  // delta T_f
  const deltatF = conductionResistanceRcylF * convectiveHeatTransfer;
  // Skin Temp Final
  const getMotorSkinTempC = deltaT - deltatF;
  return Celsius.toStandardUnit(getMotorSkinTempC);
}

export function getMotorWindingOperatingTemperatureBaseF(
  downholeMotorTemperatureF: number,
  motorLoadRatio: number,
  motorCoefInner: MotorCoefRR
) {
  // Motor Corr Motor Load
  const motorCorrMotorLoad =
    motorCoefInner.rrl0 +
    motorCoefInner.rrl1 * motorLoadRatio +
    motorCoefInner.rrl2 * Math.pow(motorLoadRatio, 2);
  // Motor Winding Operating Temp
  return downholeMotorTemperatureF + motorCorrMotorLoad;
}
